/* eslint-disable react/prop-types */
/* eslint-disable react/no-danger */
import { type FC, useEffect } from 'react'
import './GPConnect.css'
import { type ConsentForm, GP_TYPE, type Home, type User } from '@lmc/ts-common'
import { EmptyState, PageHeader, PermissionIcon } from '@lmc/ui-kit'
import { Heading } from '@lmc/ui-kit/src/general'
import useResident from 'hooks/useResident'
import { useRouter } from 'hooks/useRouter'
import { NAMESPACE } from 'locale'
import { gpConnectConstants } from 'locale/gpConnect'
import Operations from 'operations'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import {
  EmptyStateDescription,
  HereDescription
} from './components/commonStyledComponents'
import { GPConnectRecord } from './components/GPConnectRecord'
import { GP_CONSENT_TEMPLATE_ID } from './constants'

type Props = {
  type: GP_TYPE
  testId?: string
}

type ConsentFormWithConsent = ConsentForm & { hasConsented?: boolean }

export const ResidentGPConnectScreen: FC<Props> = ({ type, testId }) => {
  const { t } = useTranslation(NAMESPACE.GP_CONNECT)
  const {
    params: { id }
  } = useRouter()

  const { resident } = useResident({ id })

  const dispatch = useDispatch()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    dispatch(Operations.loadList('consent-forms', { resident: id }))
  }, [id])

  const residentConsentForms = useSelector(
    (state: { data: { ['consent-forms']: ConsentFormWithConsent[] } }) =>
      state?.data['consent-forms']
  )

  const carers = useSelector(
    (state: { data: { carers: User[] } }) => state.data.carers
  )
  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const hasGPConnectEnabled = !!home?.odsCode && !!home?.asidCode

  const hasGPConnectPermission = carers?.find(
    (carer) => carer && user && carer?.id === user?.id
  )?.gpConnectPermission

  const consentForm = residentConsentForms?.find(
    (consent: ConsentFormWithConsent) =>
      consent?.consentTemplate === GP_CONSENT_TEMPLATE_ID &&
      !consent?.isArchived
  )
  const hasSignedConsentForm = !!consentForm?.hasConsented

  const residentName =
    resident?.name.first && resident?.name.last
      ? `${resident.name.first} ${resident.name.last}`
      : undefined

  const patientSummary =
    `${residentName}'s ${t(gpConnectConstants.PATIENT_SUMMARY)}` ||
    t(gpConnectConstants.PATIENT_SUMMARY)

  const patientDetails =
    `${residentName}'s ${t(gpConnectConstants.PATIENT_DETAILS)}` ||
    t(gpConnectConstants.PATIENT_DETAILS)

  const getTitle = () => {
    if (type === GP_TYPE.SUM) {
      return patientSummary
    }
    return patientDetails
  }

  const getGPConnectState = () => {
    if (hasGPConnectEnabled && !hasSignedConsentForm) {
      return (
        <EmptyState
          title={t(gpConnectConstants.THE_PRIVACY)}
          icon={<PermissionIcon />}
          description={
            <EmptyStateDescription>
              {t(gpConnectConstants.YOU_CAN_FIND_MORE_CONSENT)}
              <Link to={`/residents/${id}/consent`}>
                <HereDescription>here</HereDescription>
              </Link>
            </EmptyStateDescription>
          }
          data-testid={
            testId
              ? `${testId}-gp-connect-consent-empty-state`
              : 'gp-connect-consent-empty-state'
          }
        />
      )
    }

    return <GPConnectRecord type={type} testId={testId} />
  }

  return (
    <MainContainer
      data-testid={testId ? `${testId}-gp-connect-screen` : 'gp-connect-screen'}
    >
      <PageHeader
        sticky
        childrenTitle={
          <HeadingStyled
            level={1}
            data-testid={
              testId ? `${testId}-gp-connect-title` : 'gp-connect-title'
            }
          >
            {getTitle()}
            {!hasGPConnectPermission && (
              <HeadingStyleNonClinical
                level={1}
                data-testid={
                  testId
                    ? `${testId}-gp-connect-non-clinical`
                    : 'gp-connect-non-clinical'
                }
              >
                {t(gpConnectConstants.NON_CLINICAL)}
              </HeadingStyleNonClinical>
            )}
          </HeadingStyled>
        }
        data-testid={
          testId ? `${testId}-gp-connect-header` : 'gp-connect-header'
        }
      />
      {getGPConnectState()}
    </MainContainer>
  )
}

const MainContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  min-width: 1024px; // NHSx requirement
  min-height: 768px; // NHSx requirement
`

const HeadingStyled = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3} !important;
`

const HeadingStyleNonClinical = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color5} !important;
  margin-left: 5px;
`
